// @flow

import * as React from "react";
import SVG from "react-inlinesvg";
import cx from "classnames";
import { matchSorter } from "match-sorter";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import Popover, { duration as popoverAnimationDuration } from "../Popover";
import TextInput from "../Input/TextInput";
import type { InputType } from "../Input/TextInput";
import ClearIcon from "../../assets/icons/other/x.svg";
import BottomCaretIcon from "../../assets/icons/other/bottomCaret.svg";
import ZoomIcon from "../../assets/icons/other/zoom.svg";
import styles from "./Select.scss";
import type { ReactElementRef } from "../../types/react";
import Preloader from "../Preloader";

export type SelectOption = {
  value: any,
  labelOnSelected?: string,
  label: string,
  searchString?: string
};

type SelectProps = {
  placeholder: string,
  options: Array<SelectOption>,
  input: InputType,
  history: {
    push: (path: string) => void
  },
  displayOption: (option: SelectOption) => React.Node,
  autoComplete?: boolean,
  isAsync?: boolean,
  additionalOption?: SelectOption,
  selectedAsyncOption?: SelectOption,
  getAsyncOptions?: (typedValue: string) => Promise<any>,
  onCleared?: () => void,
  onMount?: () => void,
  onSelect?: (value: any) => void,
  disableClear: boolean,
  onTypedValue: (typedValue: string) => void,
  popperWidth?: number,
  optionsToFilter: Array<any>,
  isTagList: boolean,
  isDisabled: boolean,
  // Needed for a special case when the selected value can be side-loaded (programmatically) and
  // cannot be found within the provided options. In such a case it is necessary to provide the
  // label which should be displayed in the Select's input, and detect function which is a predicate
  // specifying when such a sideload happened
  sideLoadedValueOptions?: {
    label: (value: any) => string,
    detect: (value: any) => boolean
  },
  additionalSideSymbolStyle?: string,
  prefilledTypedValue?: string
};

type SelectState = {
  typedValue: string,
  isSearching: boolean
};

type InputProps = {
  closePopover: void,
  onFocus: void
};

const DEBOUNCE_TIME = 1000;

class Select extends React.Component<SelectProps, SelectState> {
  static defaultProps = {
    placeholder: "",
    options: [],
    history: {
      push: () => {}
    },
    displayOption: (option: SelectOption) => option.label,
    autoComplete: false,
    isAsync: false,
    additionalOption: null,
    getAsyncOptions: null,
    selectedAsyncOption: null,
    onMount: null,
    disableClear: false,
    onTypedValue: () => {},
    popperWidth: null,
    optionsToFilter: [],
    isTagList: false,
    isDisabled: false,
    onCleared: () => {},
    additionalSideSymbolStyle: ""
  };

  optionRef: ReactElementRef<HTMLDivElement> = React.createRef();

  textInputRef: ReactElementRef<HTMLInputElement> = React.createRef();

  constructor(props: SelectProps) {
    super(props);
    const { prefilledTypedValue, isAsync } = this.props;

    this.state = {
      typedValue: "",
      isSearching: !!(isAsync && prefilledTypedValue),
      isTouched: false,
      isCleared: false
    };
  }

  willUnmount: boolean = false;

  componentDidMount() {
    const {
      onMount,
      isAsync,
      prefilledTypedValue,
      input: { value }
    } = this.props;
    if (onMount) {
      onMount();
    }

    document.addEventListener("keydown", this.handleArrowNavigation);
    this.setInitialTypedValue();
    if (isAsync && prefilledTypedValue && this.textInputRef && !value) {
      this.getAsyncOptions();
      this.textInputRef.current.focus();
    }
  }

  componentDidUpdate(prevProps: SelectProps) {
    const {
      input: { value },
      isTagList,
      options
    } = this.props;

    const valueChanged = !isTagList && value && value !== prevProps.input.value;
    const optionsChanged = !isEqual(options, prevProps.options);

    if (valueChanged || (optionsChanged && value && !isTagList)) {
      const newTypedValue = this.getNewTypedValue();
      this.setTypedValue(newTypedValue);
    }
  }

  componentWillUnmount() {
    this.willUnmount = true;

    document.removeEventListener("keydown", this.handleArrowNavigation);
  }

  setInitialTypedValue = () => {
    this.setState({ typedValue: this.getNewTypedValue() });
  };

  // checking if will not unmount to prevent setState from calling if component
  // is not in dom (related to debounce)
  setTypedValue = (typedValue: string) => {
    if (!this.willUnmount) {
      const { onTypedValue } = this.props;

      this.setState({ typedValue });
      onTypedValue(typedValue);
    }
  };

  setSearching = (isSearching: boolean) => !this.willUnmount && this.setState({ isSearching });

  getAsyncOptions = debounce(async () => {
    try {
      const { getAsyncOptions } = this.props;
      const { typedValue } = this.state;

      if (getAsyncOptions && typedValue) {
        await getAsyncOptions(typedValue);
      }

      this.setSearching(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, DEBOUNCE_TIME);

  handleArrowNavigation = (e: KeyboardEvent) => {
    const { activeElement } = document;
    const shouldHandleKeyDown = [e.target, activeElement].every(
      el =>
        el instanceof Node &&
        this.optionRef &&
        this.optionRef.current &&
        this.optionRef.current.contains &&
        this.optionRef.current.contains(el)
    );

    if (shouldHandleKeyDown) {
      let nextFocusedElement = null;

      switch (e.keyCode) {
        case 38:
          e.preventDefault();
          nextFocusedElement = activeElement && activeElement.previousSibling;
          break;
        case 40:
          e.preventDefault();
          nextFocusedElement = activeElement && activeElement.nextSibling;
          break;
        default:
      }
      if (nextFocusedElement && nextFocusedElement instanceof HTMLElement) {
        nextFocusedElement.focus();
      }
    }
  };

  handleInputValueChange = (e: KeyboardEvent) => {
    const { isAsync } = this.props;
    this.setState({ isTouched: true });

    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;

      if (typeof value === "string") {
        this.setTypedValue(value);

        if (isAsync && !!value) {
          this.setSearching(true);

          this.getAsyncOptions();
        }
      }
    }
  };

  handleSelect = (option: SelectOption, closePopover: () => void) => {
    const {
      input: { onChange },
      onSelect
    } = this.props;

    closePopover();
    // On select is executed before the value is set
    if (onSelect) {
      onSelect(option.value);
    }
    onChange(option.value);

    // remove it after animation is completed, to avoid flickering
    setTimeout(() => {
      this.setTypedValue(option.labelOnSelected || option.label);
    }, popoverAnimationDuration);
  };

  renderItems = (closePopover: () => void): React.Node => {
    const {
      displayOption,
      input: { name }
    } = this.props;
    const { isTouched } = this.state;

    const options = this.getOptionsToRender();

    if (!options.length) {
      return isTouched ? <div className={styles.notFound}>Keine Ergebnisse</div> : <div />;
    }

    return options.map((option, optionIndex) => (
      <button
        type="button"
        role="option"
        className={cx(styles.option, {
          [styles.optionSelected]: this.isValueSelected(option.value)
        })}
        // eslint-disable-next-line react/no-array-index-key
        key={`${option.label}${optionIndex}`}
        onClick={() =>
          this.callOwnOrFromProps(o => o.handleSelect)(option, closePopover, this.setTypedValue)
        }
        data-closeontab={optionIndex === options.length}
        aria-label={option.label}
        aria-selected={this.isValueSelected(option.value)}
        data-testid={`option:${name}-${option.value?.name || option.value}`} // some dropdowns have no name (undefined) but value should be there as a fallback
      >
        {displayOption(option)}
      </button>
    ));
  };

  getOptionsToRender = () => {
    const { options, autoComplete, optionsToFilter } = this.props;
    const { typedValue } = this.state;

    if (!autoComplete) {
      return this.getOptionsWithAdditional(options);
    }

    const filteredOptionsWithTypedValue = matchSorter(options, typedValue, {
      keys: ["label", "labelOnSelected", "searchString"]
    });

    const filteredOptionsWithOptionsToFilter = filteredOptionsWithTypedValue.filter(
      ({ value }) => !optionsToFilter.includes(value)
    );

    return this.getOptionsWithAdditional(filteredOptionsWithOptionsToFilter);
  };

  getOptionsWithAdditional = (options: Array<SelectOption>) => {
    const { additionalOption } = this.props;

    return [...options, ...(additionalOption ? [additionalOption] : [])];
  };

  isValueSelected = (valueToCheck: any) => {
    const {
      input: { value: currentValue }
    } = this.props;

    return isEqual(valueToCheck, currentValue);
  };

  handleOpenPopover = () => {
    const { autoComplete } = this.props;

    if (autoComplete) {
      this.setTypedValue("");
    }
  };

  handleClosePopover = () => {
    const newTypedValue = this.getNewTypedValue();

    this.setTypedValue(newTypedValue);
  };

  getNewTypedValue = () => {
    const {
      input: { value },
      isAsync,
      selectedAsyncOption,
      prefilledTypedValue
    } = this.props;
    const { isCleared } = this.state;
    let newTypedValue = "";

    if (isAsync && !value && prefilledTypedValue && !isCleared) {
      newTypedValue = prefilledTypedValue;
    }

    if (isAsync && value && !selectedAsyncOption) {
      newTypedValue = value.name;
    }

    if (isAsync && value && selectedAsyncOption) {
      const { labelOnSelected: asyncLabelOnSelected, label: asyncLabel } = selectedAsyncOption;

      newTypedValue = asyncLabelOnSelected || asyncLabel;
    } else {
      const selectedOption = this.getSelectedOption();

      if (selectedOption) {
        const { label, labelOnSelected } = selectedOption;

        newTypedValue = labelOnSelected || label;
      }
    }

    return newTypedValue;
  };

  getSelectedOption = () => {
    const { options } = this.props;

    const optionsWithAdditonal = this.getOptionsWithAdditional(options);

    return optionsWithAdditonal.find(option => this.isValueSelected(option.value));
  };

  renderContent = (closePopover: () => void) => {
    const { isAsync } = this.props;

    return (isAsync ? this.handleAsyncContent : this.renderItems)(closePopover);
  };

  handleAsyncContent = (closePopover: () => void) => {
    const { isSearching, typedValue } = this.state;

    if (!typedValue) {
      return null;
    }

    if (isSearching) {
      return (
        <div className={styles.loading} data-testid="spinner">
          <Preloader size="small" />
        </div>
      );
    }

    return this.renderItems(closePopover);
  };

  callOwnOrFromProps =
    (getFunc: any) =>
    (...args: Array<any>) => {
      const propsFunc = getFunc(this.props);
      const thisFunc = getFunc(this);

      (propsFunc || thisFunc)(...args);
    };

  clearValue = (closePopover: () => void) => {
    const {
      input: { onChange },
      onCleared
    } = this.props;

    this.setTypedValue("");
    this.setState({ isCleared: true });
    onChange(undefined);
    if (onCleared) {
      onCleared();
    }
    closePopover();

    const textInput = this.textInputRef.current;
    if (textInput) {
      textInput.blur();
    }
  };

  handleInputKeyDown = (e: KeyboardEvent, closePopover: () => void) => {
    if (e.keyCode === 40) {
      e.preventDefault();

      if (this.optionRef.current && this.optionRef.current.children.length) {
        this.optionRef.current.children[0].focus();
      }
    } else if (e.key === "Backspace") {
      const { typedValue } = this.state;
      const {
        autoComplete,
        input: { value },
        isAsync
      } = this.props;

      const shouldClearValue =
        value && ((isAsync && !typedValue) || (!isAsync && (!typedValue || !autoComplete)));

      if (shouldClearValue) {
        this.clearValue(closePopover);
      }
    }
  };

  // eslint-disable-next-line react/no-unused-prop-types
  renderInput = (inputProps: InputProps) => {
    const { typedValue } = this.state;
    const {
      placeholder,
      input: { name, onFocus, onBlur, value },
      autoComplete,
      isAsync,
      isDisabled,
      sideLoadedValueOptions
    } = this.props;

    return (
      <TextInput
        sideSymbol={() => this.renderSideSymbol(inputProps.closePopover)}
        onChange={this.handleInputValueChange}
        isDisabled={isDisabled}
        value={
          sideLoadedValueOptions && sideLoadedValueOptions.detect(value)
            ? sideLoadedValueOptions.label
            : typedValue
        }
        onFocus={e => {
          onFocus?.(e);
          inputProps.onFocus?.(e);
        }}
        onBlur={e => {
          this.setState({ isTouched: false });
          onBlur?.(e);
        }}
        data-closeonshifttab
        readOnly={!autoComplete && !isAsync}
        placeholder={placeholder}
        onKeyDown={e => this.handleInputKeyDown(e, inputProps.closePopover)}
        data-testid={`selectInput:${name}`}
        data-name={name}
        innerRef={this.textInputRef}
      />
    );
  };

  renderSideSymbol = (closePopover: () => void) => {
    const {
      input: { value },
      disableClear,
      isAsync,
      isDisabled,
      additionalSideSymbolStyle
    } = this.props;

    if (isDisabled) {
      return null;
    }

    if (value && !disableClear) {
      return (
        <button
          type="button"
          className={styles.clear}
          onClick={() => this.clearValue(closePopover)}
          data-testid="clear"
        >
          <SVG src={ClearIcon} className={cx(styles.sideSymbol, additionalSideSymbolStyle)} />
        </button>
      );
    }

    return isAsync ? (
      <SVG src={ZoomIcon} className={cx(styles.sideSymbol, additionalSideSymbolStyle)} />
    ) : (
      <SVG src={BottomCaretIcon} className={cx(styles.sideSymbol, additionalSideSymbolStyle)} />
    );
  };

  render() {
    const { popperWidth, isAsync, isDisabled } = this.props;
    const { typedValue } = this.state;

    return (
      <Popover
        input={this.renderInput}
        content={({ closePopover }) => (
          <div
            ref={this.optionRef}
            className={cx(styles.optionsWrapper, {
              [styles.hideOptionsWrapper]: isAsync && !typedValue
            })}
            role="listbox"
          >
            {this.renderContent(closePopover)}
          </div>
        )}
        onOpen={this.handleOpenPopover}
        onClose={this.handleClosePopover}
        popperWidth={popperWidth}
        isDisabled={isDisabled}
      />
    );
  }
}

export default Select;
